<template>
  <div class="mb-5">
    <ExTextInput
      label="Criteria*"
      placeholder="Add criteria"
      :value="localCriteria"
      required
      hideFloatingLabel
      dropdownSide="left"
      :dropdownItems="localDropdownItems"
      :class="[
        'criteria-input',
        { 'input-dirty': isInputDirty, 'prefix-dirty': isPrefixDirty, 'criteria-input-empty': isCriteriaEmpty }
      ]"
      :customIcon="'help_icon'"
      :showCustomIcon="showTrailingIcon"
      @input="localCriteria = $event"
      @dropdown-input="onChangeItem($event)"
      :selectedItem="localCriteriaPrefix"
      :editModal="editModal"
      @onBlur="handleCriteriaBlur"
    />
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import { maybeNormalizeCriteriaBodyEn } from "@components/utils/normalizeCriteriaBodyEn";
import store from "@/store";

const props = defineProps({
  criteria: { type: String, default: "" },
  criteriaPrefix: { type: String, default: "Can" },
  dropdownItems: { type: Array, default: () => [] },
  isInputDirty: { type: Boolean, default: false },
  isPrefixDirty: { type: Boolean, default: false },
  editModal: { type: Boolean, default: false },
  showTrailingIcon: { type: Boolean, default: false },
});

const emits = defineEmits(["update:criteria", "update:criteriaPrefix"]);

const localCriteria = ref(props.criteria);
const localCriteriaPrefix = ref(props.criteriaPrefix);
const localDropdownItems = ref(props.dropdownItems);

const ideaMDP452 = computed(() => Boolean(store.getters["featureFlags/ideaMDP452"]));

const isCriteriaEmpty = computed(() => {
  return !String(localCriteria.value || "").trim();
});

watch(localCriteria, (val) => {
  emits("update:criteria", val);
});

watch(
  () => props.criteria,
  (newVal) => {
    localCriteria.value = newVal;
  }
);

watch(
  () => props.criteriaPrefix,
  (newVal) => {
    localCriteriaPrefix.value = newVal;
  }
);

watch(
  () => props.dropdownItems,
  (newVal) => {
    localDropdownItems.value = newVal;
  }
);

const onChangeItem = (event) => {
  localCriteriaPrefix.value = event;
  emits("update:criteriaPrefix", event);
};

function handleCriteriaBlur() {
  const normalized = maybeNormalizeCriteriaBodyEn(localCriteria.value, ideaMDP452.value);
  if (normalized !== localCriteria.value) {
    localCriteria.value = normalized;
  }
}
</script>

<style>
.criteria-input .left-can-prepand {
  width: 65px !important;
  min-width: 65px;
  top: -1px;
}
.criteria-input .left-does-prepand {
  width: 72px !important; /* overrides ExTextInput default prepend width to fit "Does" prefix */
  min-width: 72px;
  top: -1px;
}
.criteria-input .left-demonstrated-prepand {
  width: 132px !important;
  min-width: 132px;
  top: -1px;
}
.criteria-input .left-proved-prepand {
  width: 80px !important;
  min-width: 80px;
  top: -1px;
}
.criteria-input.ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -60px !important;
}
.criteria-input.ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot label.v-label--active {
  margin-left: -10px !important;
}
.left-proved-prepand .v-menu__content.select-drop-list {
  margin-top: -4px;
  margin-left: -2px;
}
.code-left-wrap .v-menu__content.select-drop-list {
  padding: 5px 3px;
}

/* Purple/lavender background when only criteria is dirty (text input area only, not prefix dropdown) */
/* For "Can" prefix (default ~65px + 6px gap) */
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 71px, #D9D6FE 71px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* For "Demonstrated" prefix (~132px + 6px gap) */
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 143px, #D9D6FE 143px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* For "Proved" prefix (~80px + 6px gap) */
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, white 0%, white 91px, #D9D6FE 91px, #D9D6FE 100%) !important;
  z-index: 0 !important;
}
.criteria-input.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* Purple/lavender background when only prefix is dirty (only prefix dropdown, not text area) */
/* For "Can" prefix */
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 79px, white 65px, white 100%) !important;
  z-index: 0 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .code-select-wrapper {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand .v-select__selections {
  background: transparent !important;
}

/* For "Demonstrated" prefix */
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 147px, white 137px, white 100%) !important;
  z-index: 0 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .code-select-wrapper {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand .v-select__selections {
  background: transparent !important;
}

/* For "Proved" prefix */
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background: linear-gradient(to right, #D9D6FE 0%, #D9D6FE 95px, white 85px, white 100%) !important;
  z-index: 0 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .code-select-wrapper {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .level-picker .v-input__control fieldset {
  background: transparent !important;
}
.criteria-input.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand .v-select__selections {
  background: transparent !important;
}

/* Purple/lavender background when BOTH prefix AND criteria are dirty (entire input) */
.criteria-input.prefix-dirty.input-dirty .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden;
  background-color: #D9D6FE !important;
  z-index: 0 !important;
}
.criteria-input.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
.criteria-input.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
.criteria-input.prefix-dirty.input-dirty .code-select-wrapper {
  background: transparent !important;
}
.criteria-input.prefix-dirty.input-dirty .level-picker .v-input__control .v-input__slot {
  background: transparent !important;
}
.criteria-input.prefix-dirty.input-dirty .level-picker .v-input__control fieldset {
  background: transparent !important;
}
.criteria-input.prefix-dirty.input-dirty .v-select__selections {
  background: transparent !important;
}

.criteria-input.prefix-dirty .v-text-field .v-input__control .v-input__slot input {
  background-color: transparent !important;
}

.criteria-input.prefix-dirty .v-text-field .v-input__control .v-input__slot .v-input__prepend-inner {
  background-color: transparent !important;
}

.ex-grand-badge-picker .level-picker.v-text-field .v-input__control .v-input__slot .v-input__append-inner, .level-picker.v-text-field .v-input__control .v-select__slot{
  margin-top: -11px !important;
}

/* Red border and light-red background when criteria is empty (invalid state) — takes precedence over dirty-state lavender */
.criteria-input.criteria-input-empty .v-text-field--outlined .v-input__control fieldset {
  border: 1.5px solid #D92D20 !important;
  background: #fef3f2 !important;
  z-index: 0 !important;
}
.criteria-input.criteria-input-empty .v-text-field .v-input__control .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
</style>
